<template>
  <div class="text-widget" :class="textClasses">
    <EditableLabel
      ref="text"
      class="text-widget__editable-label"
      :class="{ 'text-widget__editable-label--adapted': adaptFontSize }"
      :value="textContent"
      use-textarea
      :style="textStyle"
      :disabled="!textEditable"
      @input="textEdited"
    />
    <div ref="sizeMonitor" class="text-widget__phantom-size-monitor">
      {{ textContent }}
    </div>
  </div>
</template>

<script>
/**
 * Text widget. Allows the user to type in whatever he wants during configuration
 */

import $ from 'jquery';
// Components
import EditableLabel from '@/components/editable-label.vue';
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import { getTextFgColor } from '@/utils/main';

export default {

  name: 'TextWidget',

  components: {
    EditableLabel,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinSocketHandler],

  props: {
    value: {
      type: String,
      default: '',
    },
    editedContent: {
      type: String,
      default: '',
    },
    fontSize: {
      type: Number,
      default: 1,
    },
    hAlign: {
      type: Number,
      default: 0,
    },
    vAlign: {
      type: Number,
      default: 0,
    },
    textEditable: Boolean,
    textScrollable: Boolean,
    adaptFontSize: Boolean,
  },

  data() {
    return {
      isMounted: false,
      resizeUpdate: 1,
    };
  },

  computed: {
    config() {
      return [
        {
          key: 'value',
          value: this.value,
          component: 'EditableLabel',
          options: {
            ref: 'label',
            id: `cfg-widget${this.widget.id}`,
            useTextarea: true,
            legend: this.$t('message.WIDGET_TEXT_CONTENT') + this.$t('message.TWO_DOTS'),
          },
        },
        {
          key: 'hAlign',
          value: this.hAlign,
          component: 'TextAlignSelector',
          options: {
            class: 'mt10',
          },
        },
        {
          key: 'vAlign',
          value: this.vAlign,
          component: 'TextAlignSelector',
          options: {
            horizontal: false,
          },
        },
        {
          key: 'fontSize',
          value: this.fontSize,
          component: 'PlusMinusButtons',
          callback: this.editFontSize,
          options: {
            disabled: this.adaptFontSize,
            class: { 'greyed-out': this.adaptFontSize },
            hasField: true,
            step: 0.1,
            legend: this.$t('message.WIDGET_TEXT_SIZE') + this.$t('message.TWO_DOTS'),
          },
        },
        {
          key: 'fontSizeHint',
          component: 'Div',
          options: {
            class: 'field-hint',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.WIDGET_TEXT_FONT_HINT', { mult: this.fontSize }),
          }],
        },
        {
          key: 'adaptFontSize',
          value: this.adaptFontSize,
          component: 'Checkbox',
          options: {
            title: this.$t('message.WIDGET_TEXT_ADAPT_FONT_SIZE'),
            class: 'block',
          },
        },
        {
          key: 'textEditable',
          value: this.textEditable,
          component: 'Checkbox',
          options: {
            title: this.$t('message.WIDGET_TEXT_ALLOW_EDIT'),
            class: 'block',
          },
        },
        {
          key: 'textScrollable',
          value: this.textScrollable,
          component: 'Checkbox',
          options: {
            title: this.$t('message.WIDGET_TEXT_ALLOW_SCROLL'),
            class: 'block',
          },
        },
        this.defaultBgColorConfig,
      ];
    },
    /** String: CSS understandable font-size. If adaptFontSize, calculate the font size */
    calculatedFontSize() {
      if (this.adaptFontSize && this.isMounted) {
        // React to a change in size of the widget
        // eslint-disable-next-line no-unused-expressions
        this.resizeUpdate;
        const el = this.$refs.sizeMonitor;
        const incrementionRate = 0.1;
        const initialSize = this.$el.offsetHeight / this.$store.getters.fontSize;
        el.style.fontSize = `${initialSize}em`;

        let fitted = false;
        let lastSize = initialSize;
        while (!fitted && lastSize > 0.5) {
          if (this.checkOverflow()) {
            lastSize -= incrementionRate;
            el.style.fontSize = `${lastSize}em`;
          } else {
            fitted = true;
            el.style.fontSize = `${lastSize}em`;
          }
        }
        return `${lastSize}em`;
      }
      return `${this.fontSize}em`;
    },
    /** Object: CSS Style applied to the EditableLabel */
    textStyle() {
      const style = {
        fontSize: this.calculatedFontSize,
        textAlign: ['left', 'center', 'right'][this.hAlign],
      };
      if (Number(this.vAlign) === 1) {
        style.top = '50%';
        style.transform = 'translateY(-50%)';
      } else if (Number(this.vAlign) === 2) {
        style.bottom = '0px';
        style.top = 'auto';
      }
      style.color = getTextFgColor(this.designOptions.bgColor);
      return style;
    },
    /** Object: Classes applied to the widget */
    textClasses() {
      return {
        scrollable: this.textScrollable,
      };
    },
    /** If content is editable outside edition mode, the test may have been edited */
    textContent() {
      return this.editedContent !== '' && this.textEditable && !this.editable
        ? this.editedContent
        : this.value;
    },
  },

  watch: {
    textContent() {
      this.$nextTick(this.resizeTextarea);
    },
    fontSize() {
      this.$nextTick(this.resizeTextarea);
    },
    textScrollable(isScrollable) {
      if (isScrollable) {
        this.resizeTextarea();
      } else {
        this.$el.scrollTop = 0;
        if (this.$refs.text.scrollToTop) {
          this.$refs.text.scrollToTop();
        }
      }
    },
  },

  mounted() {
    $(window).on(`resizeEnd.${this.uuid}`, this.resizeTextarea);
    this.resizeTextarea();
    this.isMounted = true;
  },

  methods: {
    refresh() {
      this.resizeTextarea();
    },
    resizeTextarea() {
      // Trigger the font size update
      this.resizeUpdate++;
      if (this.$refs.text && this.$refs.text.resizeTextarea) {
        this.$nextTick(this.$refs.text.resizeTextarea);
      }
    },
    /** Text edited through non-editable state */
    textEdited(value) {
      if (this.textEditable) {
        this.$emit('configChange', { key: 'editedContent', value });
      }
    },
    /** Cut number after 2nd decimal number */
    editFontSize(option, next) {
      option.value = Number(option.value.toFixed(2));
      next(option);
    },
    /** When adaptFontSize is true, check if the text overflows the container */
    checkOverflow() {
      const el = this.$refs.sizeMonitor;
      const curOverflow = el.style.overflow;
      if (!curOverflow || curOverflow === 'visible') {
        el.style.overflow = 'hidden';
      }
      const isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
      el.style.overflow = curOverflow;
      return isOverflowing;
    },
    configEditionStart(configPanel) {
      this.$nextTick(() => {
        // Might be a multiline text => expand <textarea>
        configPanel.$refs.componentsList.$refs.label.resizeTextarea();
      });
    },
  },
};

</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.text-widget.widget-component-child {
  height: 100%;
  position: relative;
  overflow: hidden;

  &.scrollable {
    overflow: auto;
  }
  .editable-label.text-widget__editable-label {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;

    textarea.textarea {
      background: transparent;
      color: inherit;
      text-align: inherit;
      margin-top: 0px;
      overflow: hidden;

      &:disabled {
        border: none;
        padding: 4px 7px;
        resize: none;
      }
    }

    &.text-widget__editable-label--adapted {
      height: 100%;

      textarea.textarea {
        padding: 0px;
      }
    }
  }

  .text-widget__phantom-size-monitor {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    visibility: hidden;
  }
}
</style>
